import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Lock } from 'lucide-react';

const PrivacyPolicy = () => {
    return (
        <div className="max-w-4xl mx-auto p-6 bg-white mt-16">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="flex justify-center items-center mb-4">
                    <Shield className="h-8 w-8 text-blue-600 mr-3" />
                    <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
                </div>
                <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
            </div>

            {/* Main Content */}
            <div className="space-y-8">
                <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-blue-600">
                    <div className="flex items-start mb-4">
                        <Lock className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">Privacy Policy</h2>
                            <p className="text-gray-700 leading-relaxed">
                                Learn With Stack LLC respects your privacy and is committed to protecting your personal information.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">We may collect the following information when you interact with our website:</p>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                        <li>Name</li>
                        <li>Email address</li>
                        <li>Phone number</li>
                        <li>Any information submitted through forms</li>
                    </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-600">
                    <div className="flex items-start">
                        <Shield className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
                            <ul className="list-disc pl-5 space-y-2 text-gray-700">
                                <li>Respond to inquiries and provide support</li>
                                <li>Send service-related updates</li>
                                <li>Send SMS communications (if you opt in)</li>
                                <li>Improve our services</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-6 border-l-4 border-purple-600">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">3. SMS Consent &amp; Data Usage</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        By providing your phone number, you consent to receive SMS messages from Learn With Stack LLC.
                    </p>
                    <div className="bg-white/70 border border-purple-200 rounded-md p-4">
                        <p className="text-gray-800 text-sm font-semibold mb-2">IMPORTANT:</p>
                        <ul className="list-disc pl-5 space-y-2 text-gray-700">
                            <li>SMS consent is <span className="font-semibold">not shared</span> with third parties or affiliates for marketing purposes</li>
                            <li>Your information is used strictly for communication related to our services</li>
                        </ul>
                    </div>
                </div>

                <div className="bg-rose-50 rounded-lg p-6 border-l-4 border-rose-600">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Opt-Out &amp; User Rights</h2>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                        <li>Opt out of SMS by replying <span className="font-semibold">STOP</span></li>
                        <li>Request updates or deletion of your data by contacting us</li>
                    </ul>
                </div>

                <div className="bg-amber-50 rounded-lg p-6 border-l-4 border-amber-500">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Cookies &amp; Tracking</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">We may use cookies and similar technologies to:</p>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                        <li>Improve website functionality</li>
                        <li>Analyze user behavior</li>
                        <li>Enhance user experience</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-4">You can disable cookies through your browser settings.</p>
                </div>

                <div className="bg-emerald-50 rounded-lg p-6 border-l-4 border-emerald-600">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Security</h2>
                    <p className="text-gray-700 leading-relaxed">
                        We implement appropriate security measures to protect your information from unauthorized access, disclosure, or misuse.
                    </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Sharing of Information</h2>
                    <p className="text-gray-700 leading-relaxed">
                        We do <span className="font-semibold">not</span> sell, rent, or share your personal information with third parties for marketing purposes.
                    </p>
                </div>

                <div className="bg-amber-50 rounded-lg p-6 border-l-4 border-amber-500">
                    <div className="flex items-start mb-4">
                        <Mail className="h-6 w-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Email Communication</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                If you contact us via email, we will use your information solely to respond to your inquiry.
                            </p>
                            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                                <p className="text-red-800 text-sm">
                                    <strong>Important:</strong> Do not send sensitive information (e.g., financial details, SSN) via email.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Contact Information</h2>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6 text-gray-700">
                        <div className="flex items-center">
                            <Mail className="h-5 w-5 text-blue-600 mr-2" />
                            <a
                                href="mailto:admin@learnwithstack.com"
                                className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                            >
                                admin@learnwithstack.com
                            </a>
                        </div>
                        <div>
                            Phone: <span className="font-medium">+1 650-484-4920</span>
                        </div>
                    </div>
                    <p className="mt-4 text-sm text-gray-600">
                        Also see our{' '}
                        <Link to="/terms-of-service" className="font-medium text-blue-600 hover:text-blue-800">
                            Terms &amp; Conditions
                        </Link>
                        .
                    </p>
                </div>
            </div>
            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-gray-200">
                <p className="text-center text-gray-500 text-sm">
                    10. Changes to Privacy Policy — We may update this Privacy Policy periodically. Updates will be posted on this page.
                </p>
            </div>
        </div>
    );
};

export default PrivacyPolicy;